import { AsyncResult, resultFactory } from '../../utils/async-result';
import {
  beforeLastOf,
  emptySpeculatedArray,
  getAtOffset,
  lastOf,
  lengthOf,
  SpeculatedArray,
} from '../../utils/speculated-array';
import { ChannelState } from '../../models/thread-updates';
import {
  conversationSlice,
  getBaseUrl,
  ThreadLookups,
  WidgetLookups,
} from '../endpoints/threads';
import { ConversationThreadId, WidgetId } from '../../models/aliases';
import { getOriginatingQueryText } from '../../utils/conversation-thread';
import { memoizeAsyncResult, memoizeByRule } from '../../utils/memoization';
import { Message, StateUpdateMessage, TextMessage } from '../../models/threads';
import { paperFinderResultSetApi } from '../endpoints/result-set';
import { paperFinderStateApi } from '../../store/endpoints/widget-state';
import { PaperFinderUIState } from '../../models/nora-ui-state';
import { ResultSet } from '../../models/result-set';
import { RootState } from '../../../../../store/store';
import { toNoraUIState, toWidgetClientState } from '../../compute/widget-state';
import { WidgetClientState } from '../../models/widget-state';

import { createSelector } from '@reduxjs/toolkit';

const INVALID_STATE_MESSAGE = 'Invalid state for NTK query';

// -------------- //
// BASE SELECTORS //
// -------------- //

export const selectConversationThread = createSelector(
  [
    (state: RootState) => state,
    (_: RootState, threadId: ConversationThreadId | undefined) => threadId,
  ],
  (
    state: RootState,
    threadId: ConversationThreadId | undefined,
  ): SpeculatedArray<Message> | undefined => {
    const threadsState = conversationSlice.selectSlice(state);
    if (threadId === undefined) {
      return undefined;
    }
    return threadsState.conversations[threadId];
  },
);

export const selectUpdateChannelState = createSelector(
  [
    (state: RootState) => state,
    (_: RootState, threadId: ConversationThreadId | undefined) => threadId,
  ],
  (
    state: RootState,
    threadId: ConversationThreadId | undefined,
  ): ChannelState | undefined => {
    const threadsState = conversationSlice.selectSlice(state);
    if (threadId === undefined) {
      return undefined;
    }
    return threadsState.channelStates[threadId];
  },
);

// this helps with memoization, see: https://reselect.js.org/usage/handling-empty-array-results
const EMPTY_WIDGET_CACHE: WidgetLookups = {
  stateIndexes: emptySpeculatedArray(),
};
export const selectWidgetCache = createSelector(
  [
    (state: RootState) => state,
    (_: RootState, threadId: ConversationThreadId | undefined) => threadId,
    (
      _: RootState,
      __: ConversationThreadId | undefined,
      widgetId: WidgetId | undefined,
    ) => widgetId,
  ],
  (
    state: RootState,
    threadId: ConversationThreadId | undefined,
    widgetId: WidgetId | undefined,
  ): WidgetLookups => {
    const threadsState = conversationSlice.selectSlice(state);

    if (widgetId === undefined && threadId !== undefined) {
      widgetId = threadsState.threadsLookups[threadId]?.lastWidgetId;
    }

    if (
      widgetId === undefined ||
      threadsState.widgetsLookups[widgetId] === undefined
    ) {
      return EMPTY_WIDGET_CACHE;
    }

    return threadsState.widgetsLookups[widgetId];
  },
);

// this helps with memoization, see: https://reselect.js.org/usage/handling-empty-array-results
const EMPTY_THREAD_CACHE: ThreadLookups = {
  textIndexes: [],
  lastWidgetId: undefined,
};
export const selectThreadCache = createSelector(
  [
    (state: RootState) => state,
    (_: RootState, threadId: ConversationThreadId | undefined) => threadId,
  ],
  (
    state: RootState,
    threadId: ConversationThreadId | undefined,
  ): ThreadLookups => {
    const threadsState = conversationSlice.selectSlice(state);
    if (
      threadId === undefined ||
      threadsState.threadsLookups[threadId] === undefined
    ) {
      return EMPTY_THREAD_CACHE;
    }

    return threadsState.threadsLookups[threadId];
  },
);

// ----------------------------- //
// DERIVED COMPUTATION SELECTORS //
// ----------------------------- //

export const selectPaperFinderAllMessages = createSelector(
  [
    (_: RootState, threadId: ConversationThreadId | undefined) => threadId,
    selectConversationThread,
  ],
  memoizeAsyncResult(
    (
      threadId: ConversationThreadId | undefined,
      conversationThread: SpeculatedArray<Message> | undefined,
    ): AsyncResult<SpeculatedArray<Message>> => {
      if (threadId === undefined) {
        return resultFactory.uninitialized();
      }
      if (conversationThread !== undefined) {
        return resultFactory.successful(conversationThread);
      } else {
        return resultFactory.loading();
      }
    },
    // MEMOIZATION RULE: if it's the same thread, and the list size is the same
    ([, threadId], [, lastThreadId], messages, lastMessages) =>
      threadId === lastThreadId &&
      lengthOf(messages) === lengthOf(lastMessages),
  ),
);

export const selectPaperFinderNumMessages = createSelector(
  [
    selectPaperFinderAllMessages,
    (_: RootState, threadId: ConversationThreadId | undefined) => threadId,
  ],
  memoizeAsyncResult(
    (
      allMessagesResult: AsyncResult<SpeculatedArray<Message>>,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _: ConversationThreadId | undefined,
    ): AsyncResult<number> => {
      if (allMessagesResult.isSuccess) {
        return resultFactory.successful(lengthOf(allMessagesResult.data));
      }
      return allMessagesResult;
    },

    // MEMOIZATION RULE: if it's the same thread, and number of messages is identical
    ([, threadId], [, lastThreadId], result, lastResult) =>
      threadId === lastThreadId && result === lastResult,
  ),
);

export const selectPaperFinderTextMessages = createSelector(
  [
    selectPaperFinderAllMessages,
    (_: RootState, threadId: ConversationThreadId | undefined) => threadId,
    selectThreadCache,
  ],
  memoizeAsyncResult(
    (
      allMessages: AsyncResult<SpeculatedArray<Message>>,
      threadId: ConversationThreadId | undefined,
      threadCache,
    ): AsyncResult<Array<TextMessage>> => {
      if (threadId === undefined) {
        return resultFactory.uninitialized();
      }

      if (allMessages.isSuccess) {
        const textMessages = threadCache.textIndexes.map(
          (i: number) => getAtOffset(allMessages.data, i) as TextMessage,
        );

        return resultFactory.successful(textMessages);
      }

      return allMessages;
    },
    // MEMOIZATION RULE: if it's the same thread, and the list size is the same
    ([, threadId], [, lastThreadId], result, lastResult) =>
      threadId === lastThreadId && result.length === lastResult.length,
  ),
);

export const selectPaperFinderWidgetState = createSelector(
  [
    (state: RootState) => state,
    (_: RootState, threadId: ConversationThreadId | undefined) => threadId,
    (
      _: RootState,
      __: ConversationThreadId | undefined,
      widgetId: WidgetId | undefined,
    ) => widgetId,
    selectPaperFinderAllMessages,
    selectThreadCache,
  ],
  memoizeAsyncResult(
    (
      state: RootState,
      threadId: ConversationThreadId | undefined,
      widgetId: WidgetId | undefined,
      allMessagesResult: AsyncResult<SpeculatedArray<Message>>,
      threadCache: ThreadLookups,
    ): AsyncResult<WidgetClientState | undefined> => {
      if (threadId === undefined) {
        return resultFactory.uninitialized<WidgetClientState | undefined>();
      }

      if (!allMessagesResult.isSuccess) {
        return allMessagesResult;
      }

      if (widgetId === undefined) {
        widgetId = threadCache.lastWidgetId;
      }

      const widgetCaches: WidgetLookups = selectWidgetCache(
        state,
        threadId,
        widgetId,
      );

      if (lengthOf(widgetCaches.stateIndexes) === 0) {
        return resultFactory.successful(undefined);
      }

      const allMessages = allMessagesResult.data;

      const lastStateMessageOffset = lastOf(widgetCaches.stateIndexes);
      const lastStateMessage = getAtOffset(
        allMessages,
        lastStateMessageOffset,
      ) as StateUpdateMessage;

      if (lastStateMessage === undefined) {
        return resultFactory.successful(undefined);
      }

      const {
        widgetId: lastMessageWidgetId,
        stateId,
        status,
      } = lastStateMessage;

      if (status === undefined) {
        return resultFactory.failed({
          message: 'Outdated widget state, can not display data',
        });
      }
      const userQuery =
        getOriginatingQueryText(allMessages, widgetCaches) ?? '';

      const baseUrl = getBaseUrl(state, threadId);

      const widgetStateResult =
        paperFinderStateApi.endpoints.widgetState.select({
          baseUrl,
          stateId,
        })(state);

      let resultSetNeeded = false;

      const resultSetResult =
        paperFinderResultSetApi.endpoints.resultSet.select({
          baseUrl,
          widgetId: lastMessageWidgetId,
        })(state);

      if (status === 'completed') {
        resultSetNeeded = true;

        if (resultSetResult.isError) {
          if (resultSetResult.error) {
            return resultFactory.failed(resultSetResult.error);
          } else {
            // adding 'retry' to `createAPI` causes the `result.error` type to become `unknown`
            return resultFactory.failed({ message: 'Unknown RTK retry error' });
          }
        }
      }

      if (widgetStateResult.isError) {
        if (widgetStateResult.error) {
          return resultFactory.failed(widgetStateResult.error);
        } else {
          // adding 'retry' to `createAPI` causes the `result.error` type to become `unknown`
          return resultFactory.failed({ message: 'Unknown RTK retry error' });
        }
      }

      const currentChannelState = selectUpdateChannelState(state, threadId);
      // while the new widget sub resources aren't loaded yet, we consider the previous one as the correct state
      const widgetStateNotReady =
        widgetStateResult.isLoading || widgetStateResult.isUninitialized;
      const resultSetNotReady =
        resultSetNeeded &&
        (resultSetResult.isLoading || resultSetResult.isUninitialized);

      if (
        (widgetStateNotReady || resultSetNotReady) &&
        lengthOf(widgetCaches.stateIndexes) > 1
      ) {
        const beforeLastStateMessageOffset = beforeLastOf(
          widgetCaches.stateIndexes,
        );
        const beforeLastStateMessage = getAtOffset(
          allMessages,
          beforeLastStateMessageOffset,
        ) as StateUpdateMessage;

        if (
          beforeLastStateMessage &&
          beforeLastStateMessage.widgetId === lastMessageWidgetId
        ) {
          const { data: prevWidgetState } =
            paperFinderStateApi.endpoints.widgetState.select({
              baseUrl,
              stateId: beforeLastStateMessage.stateId,
            })(state);

          let papers: ResultSet | undefined;
          if (beforeLastStateMessage.status === 'completed') {
            papers = paperFinderResultSetApi.endpoints.resultSet.select({
              baseUrl,
              widgetId: beforeLastStateMessage.widgetId,
            })(state).data;
          } else {
            papers = [];
          }
          if (prevWidgetState !== undefined && papers !== undefined) {
            const r = resultFactory.successful(
              toWidgetClientState(
                papers,
                beforeLastStateMessage,
                prevWidgetState,
                allMessages,
                userQuery,
                currentChannelState,
              ),
            );
            return r;
          }
        }
      }

      if (widgetStateNotReady || resultSetNotReady) {
        return resultFactory.loading();
      }

      if (widgetStateResult.isSuccess) {
        return resultFactory.successful(
          toWidgetClientState(
            resultSetResult.data ?? [],
            lastStateMessage,
            widgetStateResult.data,
            allMessages,
            userQuery,
            currentChannelState,
          ),
        );
      }

      // we've checked all possible states of NTK query, we should never reach this
      return resultFactory.failed({ message: INVALID_STATE_MESSAGE });
    },
    // MEMOIZATION RULE: if it's the same state, and the same sync status
    (
      [, threadId, widgetId],
      [, lastThreadId, lastWidgetId],
      result,
      lastResult,
    ) =>
      threadId === lastThreadId &&
      widgetId === lastWidgetId &&
      result?.stateId === lastResult?.stateId &&
      // we can have the same state but the sync status change
      // so me must take this into consideration as well or we can
      // get into situations where the ui doesn't get a syncStatus
      // change because it happens during the same state version
      result?.syncStatus?.type === lastResult?.syncStatus?.type &&
      result?.prevStateId === lastResult?.prevStateId &&
      result?.nextStateId === lastResult?.nextStateId,
  ),
);

export const selectPaperFinderUIStateForWidget = createSelector(
  [selectPaperFinderWidgetState],
  memoizeByRule(
    (
      clientState: AsyncResult<WidgetClientState | undefined>,
    ): PaperFinderUIState | undefined => {
      if (
        clientState.isUninitialized ||
        clientState.isLoading ||
        clientState.isError ||
        clientState.data === undefined
      ) {
        return undefined;
      }
      return toNoraUIState(clientState.data);
    },
    // MEMOIZATION RULE: same state
    (_, __, result, lastResult) => {
      return result?.stateId === lastResult?.stateId;
    },
  ),
);
